import { without } from 'es-toolkit';
import { createSelector } from '@reduxjs/toolkit';

import { hasTrussDesignPermission } from '@paragon/login-tools';

import {
  ALL_SELECTABLE_ITEM_TYPES,
  isConsideredLayoutItem,
  isSelectableItemType,
  SelectableItemType,
} from '../../library/selection/types';
import { getActiveSelectionWindowTargetType } from '../../library/selection/window-tool/selectors';
import { RootState } from '..';
import { EDIT_IN_PROGRESS_TYPES } from '../slices/editorSlice';
import {
  ALL_RIGHT_CLICKABLE_CONTAINER_TYPES,
  ALL_RIGHT_CLICKABLE_ITEM_TYPES,
  LAYOUT_RIGHT_CLICKABLE_CONTAINER_TYPES,
  LAYOUT_RIGHT_CLICKABLE_ITEM_TYPES,
  RightClickableContainerType,
  RightClickableItemType,
} from '../slices/viewportSlice';
import { getBasicInsertionInformation, isBasicInsertionModeActive } from './basicInsertionSelectors';
import { isInPreviewMode } from './isInPreviewModeSelector';
import { isLayoutModeViewOnly, isLayoutViewActive } from './layoutModeSelectors';
import { getPickingObjectInfoType, isPickingActive } from './pickingSelectors';
import { getRightSidebarInfo } from './rightToolbarSelectors';
import { inViewOnlyMode } from './viewOnlyModeSelectors';

const EDITS_THAT_BLOCK_SELECTION = without(EDIT_IN_PROGRESS_TYPES, 'InsertingMember');

const isRaycastSelectionDisabled = createSelector(
  (state: RootState) => state.editor.editInProgress,
  getPickingObjectInfoType,
  (editInProgress, pickingObjectType) => {
    // Reference line intersections are handled by the intersection snaps, not ray-casting.
    const isPickingReferenceLineIntersection = pickingObjectType === 'pickingReferenceLineIntersection';
    return (
      (editInProgress && EDITS_THAT_BLOCK_SELECTION.includes(editInProgress)) || isPickingReferenceLineIntersection
    );
  },
);

const isBasicInsertingMember = createSelector(
  getBasicInsertionInformation,
  (basicInsertionInfo) => basicInsertionInfo.inserting === 'member' && isBasicInsertionModeActive(basicInsertionInfo),
);

export const getCurrentlySelectableItemTypes = createSelector(
  isRaycastSelectionDisabled,
  isBasicInsertingMember,
  (state: RootState) => state.application.insertingObjectInfo,
  (state: RootState) => state.application.transformingObjectsInfo,
  isPickingActive,
  (state: RootState) => state.application.inDimensionMode,
  (state: RootState) => !!state.application.rewebbingInfo,
  isLayoutViewActive,
  getActiveSelectionWindowTargetType,
  (
    isRaycastSelectionDisabled,
    isBasicInsertingMember,
    insertingObjectInfo,
    transformingObjectsInfo,
    isPicking,
    inDimensionMode,
    isRewebbing,
    inLayoutMode,
    selectionWindowTarget,
  ): Set<SelectableItemType> => {
    if (isRaycastSelectionDisabled) return new Set();

    // FIXME: this is masking a bug where objects could otherwise be selected during forced picking modes, as the
    //   picking mode is technically cleared before this selector is hit by the selection layer
    if (transformingObjectsInfo != null) return new Set();

    // Selecting things in the scene can't occur during member insertion when the info tab is open, because the click
    //  event is re-purposed for selecting snaps, which isn't part of ray-casting.
    if (isBasicInsertingMember) return new Set();

    // In layout mode there is never a good reason to select a different item while inserting something, and even if
    //   harmless, it can be confusing to the user.
    if (
      insertingObjectInfo?.type &&
      isSelectableItemType(insertingObjectInfo.type) &&
      isConsideredLayoutItem(insertingObjectInfo.type)
    ) {
      return new Set();
    }

    switch (insertingObjectInfo?.type) {
      case 'splice':
      case 'bearing':
        return new Set();
    }

    // Similarly in layout mode there's never a good reason to allow selecting while picking is occurring. This
    //   restriction is likely also applicable to design mode as well, but that would require more research and is less
    //   necessary, since ThreeJsSceneActions is able to prevent selection before `selectEntry` is ever called.
    if (inLayoutMode && isPicking) return new Set();

    if (inDimensionMode) return new Set(['componentDesign', 'productionComponentInstance']);
    if (isRewebbing || insertingObjectInfo?.type === 'componentDesign')
      return new Set(['profileJoint', 'webJoint', 'webJointSegment', 'webSegment']);

    if (selectionWindowTarget) {
      return new Set([selectionWindowTarget]);
    }

    return new Set(ALL_SELECTABLE_ITEM_TYPES);
  },
);

export const getCurrentlyRightClickableItemTypes = createSelector(
  isRaycastSelectionDisabled,
  inViewOnlyMode,
  isLayoutModeViewOnly,
  hasTrussDesignPermission,
  isBasicInsertingMember,
  (state: RootState) => state.application.inDimensionMode,
  (state: RootState) => !!state.application.rewebbingInfo,
  isInPreviewMode,
  getRightSidebarInfo,
  (
    isRaycastSelectionDisabled,
    componentIsViewOnly,
    layoutIsViewOnly,
    hasPermission,
    isBasicInsertingMember,
    inDimensionMode,
    isRewebbing,
    inPreviewMode,
    rightSidebarInfo,
  ): Set<RightClickableItemType | RightClickableContainerType> => {
    if (isRaycastSelectionDisabled || !hasPermission) return new Set();

    // The `componentIsViewOnly` selector returns `true` while inserting a component design, so we need to consider that
    //   first
    if (inPreviewMode || isRewebbing) return new Set(['webSegment', 'webJointSegment']);
    if (componentIsViewOnly) return new Set();

    // Include `null` if right-clicking in empty space should bring up a context menu
    if (inDimensionMode) return new Set([null]);
    if (isBasicInsertingMember) return new Set(['referenceLine', null]);

    if (rightSidebarInfo.mode !== 'none' && rightSidebarInfo.mode !== 'normal') return new Set();

    if (layoutIsViewOnly)
      return new Set([
        ...without(ALL_RIGHT_CLICKABLE_ITEM_TYPES, ...LAYOUT_RIGHT_CLICKABLE_ITEM_TYPES),
        ...without(ALL_RIGHT_CLICKABLE_CONTAINER_TYPES, ...LAYOUT_RIGHT_CLICKABLE_CONTAINER_TYPES),
      ]);

    return new Set([...ALL_RIGHT_CLICKABLE_ITEM_TYPES, ...ALL_RIGHT_CLICKABLE_CONTAINER_TYPES]);
  },
);
